import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import * as React from "react";

import { requestDealExtension, withdrawDealExtension, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { DateRowInput } from "@/components/ui/date-row-input";
import { Textarea } from "@/components/ui/textarea";
import { dealToasts, useToast } from "@/components/ui/toast";
import { apiErrorMessage } from "@/lib/api-error";

/** DD/MM/YYYY (Australian). */
function formatAuDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/** Tomorrow in YYYY-MM-DD — an extension must at least move past today. */
function minRequestedDate(): string {
  return new Date(Date.now() + 86400000).toISOString().slice(0, 10);
}

/**
 * Earliest selectable extension date (CHP-525): the day after the deal's
 * current expiry, or tomorrow if that is later (or the deal has no close date).
 * An extension that lands on or before the current expiry is meaningless, so
 * the picker's `min` blocks it — the backend enforces the same rule.
 */
export function minExtensionDate(deal: Deal): string {
  const tomorrow = minRequestedDate();
  if (!deal.target_close_date) return tomorrow;
  const d = new Date(`${deal.target_close_date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  const dayAfterExpiry = d.toISOString().slice(0, 10);
  return dayAfterExpiry > tomorrow ? dayAfterExpiry : tomorrow;
}

/** Review-r2 (5541d74c): an extension is capped at 90 days. The new expiry may
 * not be selected beyond 90 days from the deal's CURRENT expiry. Returns the
 * latest selectable date (YYYY-MM-DD), or undefined when the current expiry is
 * unknown (no cap we can compute). */
export function maxExtensionDate(currentExpiry: string | null): string | undefined {
  if (!currentExpiry) return undefined;
  // Compute the +90 cap in UTC (mirrors minExtensionDate). The previous
  // `new Date(currentExpiry)` + local `setDate` mixed a UTC-parsed date-only
  // string with local-time arithmetic, so in a Sydney DST-crossing window the
  // cap landed one day short — blocking the exact +90 date the backend accepts
  // (CHP-525 minor).
  const d = new Date(`${currentExpiry}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setUTCDate(d.getUTCDate() + 90);
  return d.toISOString().slice(0, 10);
}

export interface DealExtensionFieldsProps {
  requestedDate: string;
  onRequestedDateChange: (value: string) => void;
  justification: string;
  onJustificationChange: (value: string) => void;
  disabled?: boolean;
  /** Earliest selectable date (YYYY-MM-DD). Defaults to tomorrow; hosts pass
   * `minExtensionDate(deal)` so a date on/before the current expiry is blocked. */
  minDate?: string;
  /** Review-r2 (5541d74c): latest selectable date — caps the picker at the
   * deal's current expiry + 90 days. Omit to leave the picker uncapped. */
  max?: string;
}

/**
 * The extension request form fields — date picker + mandatory justification —
 * extracted from DealExpiryDecisionOverlay (CHP-225) so the deal detail
 * drawer's "Request Extension" action renders the exact same form. Controlled:
 * each host owns the state and its own submit affordance (the overlay's shared
 * "Confirm Selection" button vs the drawer's "Submit Request").
 */
export function DealExtensionFields({
  requestedDate,
  onRequestedDateChange,
  justification,
  onJustificationChange,
  disabled = false,
  minDate,
  max,
}: DealExtensionFieldsProps) {
  return (
    <div className="space-y-3">
      <div className="block text-sm">
        <label htmlFor="extension-requested-date" className="font-medium">
          Proposed expiry date
        </label>
        {/* CHP-309/CHP-396: the whole row is the click target (shared
            DateRowInput). It is purely an enhancement over the native input —
            the input keeps its own keyboard focus/tab/Enter behaviour.
            Review-r2 (5541d74c): `max` caps the picker at current expiry + 90d. */}
        <DateRowInput
          id="extension-requested-date"
          value={requestedDate}
          onChange={(e) => onRequestedDateChange(e.target.value)}
          min={minDate ?? minRequestedDate()}
          max={max}
          disabled={disabled}
          wrapperClassName="mt-1"
        />
      </div>
      <label className="block text-sm">
        Reason for extension (Required)
        <Textarea
          value={justification}
          onChange={(e) => onJustificationChange(e.target.value)}
          maxLength={2000}
          rows={3}
          disabled={disabled}
          placeholder="Briefly explain why an extension is required."
          className="mt-1 w-full"
        />
      </label>
    </div>
  );
}

/**
 * "Withdraw request" in the deal detail Actions section (CHP-1179,
 * extension-pending deals only). Before this, a deal in EXTENSION_PENDING
 * offered the owner no action at all — the only exits were a manager's
 * approve/deny. Expands inline to a confirm box (mirrors the request form's
 * collapsed→expanded pattern); on success invalidates ["deals"] so the open
 * drawer refetches and flips back to LIVE.
 */
export function DealExtensionWithdrawAction({ deal }: { deal: Deal }) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const withdraw = useMutation({
    mutationFn: () => withdrawDealExtension(accessToken!, deal.id),
    onSuccess: () => {
      setOpen(false);
      toast(dealToasts.extensionWithdrawn());
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Withdrawing the request failed. Refresh and try again.")),
  });

  if (deal.deal_status !== "extension_pending") return null;

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        Withdraw extension request
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-l-4 border-border border-l-primary bg-primary/5 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">
        Extension request
      </p>
      <h3 className="mt-1 flex items-center gap-2 text-sm font-semibold">
        <CalendarClock className="size-4 text-primary" aria-hidden />
        Withdraw the extension request for <span className="font-medium">{deal.client_name}</span>
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Requested expiry: {formatAuDate(deal.extension_requested_date)}. Withdrawing returns the
        deal to its original expiry of {formatAuDate(deal.original_close_date)} and removes the
        request from the review queue. You can request a new extension later.
      </p>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          onClick={() => withdraw.mutate()}
          disabled={withdraw.isPending}
          loading={withdraw.isPending}
          loadingLabel="Withdrawing…"
        >
          Withdraw request
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setOpen(false)}
          disabled={withdraw.isPending}
        >
          Keep request
        </Button>
      </div>
    </div>
  );
}

/**
 * "Request Extension" in the deal detail Actions section (live deals only).
 * Before this, the only entry point to an extension was the expiring-soon feed
 * card inside the 14-day window — a live deal outside it had no path. Expands
 * inline to the shared DealExtensionFields; on success invalidates ["deals"]
 * so the open drawer's detail query refetches and flips to EXTENSION_PENDING.
 */
export function DealExtensionRequestAction({ deal }: { deal: Deal }) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [open, setOpen] = React.useState(false);
  const [requestedDate, setRequestedDate] = React.useState("");
  const [justification, setJustification] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const extend = useMutation({
    mutationFn: () =>
      requestDealExtension(accessToken!, deal.id, {
        requested_date: requestedDate,
        justification: justification.trim(),
      }),
    onSuccess: () => {
      setOpen(false);
      toast(dealToasts.extensionRequested());
      // Prefix-invalidates the drawer's ["deals", "detail", …] query too, so
      // an open drawer refetches and shows the EXTENSION_PENDING status.
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Extension request failed. Refresh and try again.")),
  });

  if (deal.deal_status !== "live") return null;

  const submitDisabled = extend.isPending || !requestedDate || !justification.trim();

  // CHP-525: a proposed date on/before the current expiry is rejected by the
  // backend; guard here too so the user gets the reason without a round-trip.
  const submit = () => {
    if (submitDisabled) return;
    if (deal.target_close_date && requestedDate <= deal.target_close_date) {
      setError("The proposed date must be after the current expiry date.");
      return;
    }
    setError(null);
    extend.mutate();
  };

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setError(null);
          setRequestedDate("");
          setJustification("");
          setOpen(true);
        }}
      >
        Request extension
      </Button>
    );
  }

  return (
    // CHP-317: styled distinctly as an EXTENSION form (accent border + eyebrow
    // + calendar icon) so it can't be mistaken for the neutral closed-won form.
    <div className="rounded-md border border-l-4 border-border border-l-primary bg-primary/5 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">
        Extension request
      </p>
      <h3 className="mt-1 flex items-center gap-2 text-sm font-semibold">
        <CalendarClock className="size-4 text-primary" aria-hidden />
        Extend the expiry for <span className="font-medium">{deal.client_name}</span>
      </h3>
      {/* Review-r2 (4c46d0c1): designer copy. */}
      <p className="mt-1 text-sm text-muted-foreground">
        Current expiry: {formatAuDate(deal.target_close_date)}. Extensions are capped at 90 days.
      </p>
      <div className="mt-3">
        <DealExtensionFields
          requestedDate={requestedDate}
          onRequestedDateChange={setRequestedDate}
          justification={justification}
          onJustificationChange={setJustification}
          disabled={extend.isPending}
          minDate={minExtensionDate(deal)}
          // Review-r2 (5541d74c): can't select beyond current expiry + 90 days.
          max={maxExtensionDate(deal.target_close_date)}
        />
      </div>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          onClick={submit}
          disabled={submitDisabled}
          loading={extend.isPending}
          loadingLabel="Updating…"
        >
          Submit request
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setOpen(false)}
          disabled={extend.isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
